import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState, type FormEvent } from 'react';
import { ErrorBanner } from '@/components/form';
import { AuthCard } from '@/components/marketing/auth-card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { auth } from '@/lib/auth';
import { useT } from '@/lib/i18n';
import { safeRedirect } from '@/lib/safe-redirect';

export const Route = createFileRoute('/login')({
  component: LoginPage,
  validateSearch: (s: Record<string, unknown>): { redirect?: string } => {
    const redirect = safeRedirect(s['redirect']);
    return redirect ? { redirect } : {};
  },
});

function LoginPage() {
  const { redirect = '/dashboard' } = Route.useSearch();
  const navigate = useNavigate();
  const t = useT();
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [signedIn, setSignedIn] = useState(false);

  // Someone already signed in gets a link straight in.
  useEffect(() => {
    void auth.getUser().then((u) => setSignedIn(Boolean(u)));
  }, []);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!auth.enabled) {
      void navigate({ href: redirect });
      return;
    }
    setBusy(true);
    setFailed(false);
    void auth.signIn(redirect, { email: email.trim() }).catch(() => {
      // the identity provider is unreachable: say so instead of re-enabling the button silently
      setBusy(false);
      setFailed(true);
    });
  };

  return (
    <AuthCard
      title={t('publicPages.login.title')}
      footer={
        <span className="text-brand-slate">
          {t('publicPages.login.newTo')}{' '}
          <Link to="/signup" className="font-medium text-primary hover:underline">
            {t('publicPages.login.createAccount')}
          </Link>
        </span>
      }
    >
      {signedIn ? (
        <div className="mb-5 rounded-md bg-brand-violet-50 px-4 py-3 text-sm">
          {t('publicPages.login.alreadySignedIn')}{' '}
          <a href={redirect} className="font-semibold text-primary hover:underline">
            {t('publicPages.login.open')}
          </a>
        </div>
      ) : null}
      <form onSubmit={submit} className="space-y-4">
        <ErrorBanner>{failed ? t('publicPages.login.failed') : null}</ErrorBanner>
        <label className="block">
          <span className="text-xs font-semibold text-brand-slate">
            {t('publicPages.login.email')}
          </span>
          <Input
            type="email"
            autoComplete="email"
            autoFocus
            placeholder={t('publicPages.login.emailPlaceholder')}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 h-10 bg-white"
            data-testid="login-email"
          />
        </label>
        <Button type="submit" className="h-10 w-full font-semibold" disabled={busy}>
          {busy ? t('publicPages.login.opening') : t('publicPages.login.continue')}
        </Button>
        <p className="text-center text-xs text-brand-slate">
          {t('publicPages.login.passwordNext')}
        </p>
      </form>
    </AuthCard>
  );
}
